import { useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OrangeHighlightButton from "../../elements/OrangeHighlightButton";

type AccordionItem = {
  title: string;
  content: string;
};

type DiscoverPotentialSectionProps = {
  title: string;
  description: string;
  description1?: string;
  image: string;
  accordionItems?: AccordionItem[];
  buttonText?: string;
  reverse?: boolean;
};

export const DiscoverPotentialSection = ({
  title,
  description,
  description1,
  image,
  accordionItems,
  buttonText,
  reverse = false,
}: DiscoverPotentialSectionProps) => {
  const history = useHistory();
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language || "en";

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const handleRedirection = () => {
    const path =
      currentLanguage === "en" ? "/nursery" : `/${currentLanguage}/nursery`;
    history.push(path);
  };

  return (
    <Box component="section" py={6} px={{ xs: 2, md: "15%" }}>
      <Grid container spacing={4} direction={reverse ? "row-reverse" : "row"}>
        <Grid item xs={12} md={6}>
          <Box
            component="img"
            src={image}
            alt="Potential visual"
            sx={{ width: "100%", borderRadius: 2 }}
          />
        </Grid>

        <Grid
          item
          xs={12}
          md={6}
          display="flex"
          flexDirection="column"
          alignItems={isMobile ? "center" : "start"}
        >
          <Typography
            variant="h2"
            fontWeight="800"
            textAlign={isMobile ? "center" : "start"}
            color={theme.palette.text.secondary}
            gutterBottom
          >
            {title}
          </Typography>

          <Typography
            variant="h5"
            fontWeight={400}
            textAlign={isMobile ? "center" : "start"}
            color={theme.palette.text.primary}
            paragraph
          >
            {description}
          </Typography>

          {description1 && (
            <Typography
              variant="h5"
              fontWeight={400}
              textAlign={isMobile ? "center" : "start"}
              color={theme.palette.text.primary}
              paragraph
            >
              {description1}
            </Typography>
          )}

          {accordionItems &&
            accordionItems.map((item, index) => (
              <Accordion
                key={index}
                expanded={expanded === item.title}
                onChange={handleChange(item.title)}
                disableGutters
                sx={{ boxShadow: "none", mb: 1 }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon
                      sx={{
                        color:
                          expanded === item.title
                            ? theme.palette.secondary.main
                            : theme.palette.text.primary,
                      }}
                    />
                  }
                >
                  <Typography
                    fontWeight={700}
                    color={
                      expanded === item.title
                        ? theme.palette.secondary.main
                        : theme.palette.text.primary
                    }
                  >
                    {item.title}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography color={theme.palette.text.primary}>
                    {item.content}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}

          {buttonText && (
            <OrangeHighlightButton onClick={handleRedirection}>
              {buttonText}
            </OrangeHighlightButton>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};
